import React, { useCallback, useState } from 'react';
import { App } from 'antd';

import MakeActiveTagModal from './MakeActiveTagModal';
import useActiveTag from '../../hooks/useActiveTag';
import { MAKE_ACTIVE_LABEL, STOP_ADDING_LABEL, activeTagRefusal } from '../../utils/activeTag';
import { isPrivateTag, tagIdOf } from '../../utils/tags';

/**
 * WHERE THE ACTIVE TAG IS TURNED ON AND OFF (docs/plans/tags-active-tag-proposal.md,
 * build call b52): a PRIVATE tag's ⋯ on its tag page and in Account › Private
 * tags. Never on the item form (it would read as "tag this item").
 *
 *   const { actionsFor, dialog } = useActiveTagMenu();
 *   menuActions={[...others, ...actionsFor(tag)]}   …   {dialog}
 *
 * `actionsFor(tag)` is one Record-group entry: **Make active…** (the dialog:
 * the optional end date, and — when another tag is on — that this one takes
 * its place) or, while this tag is the active one, **Stop adding it**, at
 * once. Nothing for a collection tag or an item tag (private tags only, b47).
 * `dialog` is the Make active… dialog, to render once beside the menu.
 */
export default function useActiveTagMenu() {
	const { message } = App.useApp();
	const { activeTag, isActive, makeActive, stop } = useActiveTag();
	const [target, setTarget] = useState(null);

	const stopAdding = useCallback(async (tag) => {
		try {
			await stop();
			message.success(`Stopped adding “${tag?.name || 'this tag'}”.`);
		} catch (error) {
			message.error(activeTagRefusal(error));
		}
	}, [stop, message]);

	const onMakeActive = useCallback(async (tagId, options) => {
		await makeActive(tagId, options);
		message.success(`Adding “${target?.name || 'this tag'}” to everything you add.`);
	}, [makeActive, message, target]);

	const actionsFor = useCallback((tag) => {
		if (!tag || !isPrivateTag(tag) || !tagIdOf(tag)) return [];
		if (isActive(tagIdOf(tag))) {
			return [{ key: 'active-tag-stop', group: 'record', glyph: 'remove', label: STOP_ADDING_LABEL, onClick: () => stopAdding(tag) }];
		}
		return [{ key: 'active-tag-make', group: 'record', glyph: 'tag', label: MAKE_ACTIVE_LABEL, onClick: () => setTarget(tag) }];
	}, [isActive, stopAdding]);

	const dialog = (
		<MakeActiveTagModal
			open={Boolean(target)}
			tag={target}
			current={activeTag}
			onMakeActive={onMakeActive}
			onClose={() => setTarget(null)}
		/>
	);

	return { actionsFor, dialog, activeTag };
}
